import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import type { Transaction } from '@/types';

interface DrillDownTransactionTableProps {
  transactions: Transaction[];
}

/**
 * Table component for displaying drill-down transaction results.
 * Shows transaction details with color-coded categories and status badges.
 */
export function DrillDownTransactionTable({ transactions }: DrillDownTransactionTableProps) {
  const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'paid':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'overdue':
        return 'destructive';
      case 'cancelled':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status: string): string => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'paid':
        return 'Pago';
      case 'pending':
        return 'Pendente';
      case 'overdue':
        return 'Atrasado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Descrição</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Centro de Custo</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Valor</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((transaction) => {
            const statusValue = transaction.status ?? (transaction.is_paid ? 'PAID' : 'PENDING');

            return (
              <TableRow key={transaction.id}>
              <TableCell className="whitespace-nowrap">
                {format(new Date(transaction.date), 'dd/MM/yyyy', { locale: ptBR })}
              </TableCell>
              <TableCell className="max-w-xs truncate">
                {transaction.description}
              </TableCell>
              <TableCell>
                {transaction.categories ? (
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: transaction.categories.color || '#94a3b8' }}
                    />
                    <span className="text-sm">{transaction.categories.name}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">Sem categoria</span>
                )}
              </TableCell>
              <TableCell>
                {transaction.cost_centers ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {transaction.cost_centers.code}
                    </span>
                    <span className="text-sm">{transaction.cost_centers.name}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </TableCell>
              <TableCell>
                <Badge variant={getStatusVariant(statusValue)}>
                  {getStatusLabel(statusValue)}
                </Badge>
              </TableCell>
              <TableCell className={`text-right font-medium ${
                transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
              }`}>
                {transaction.type === 'income' ? '+' : '-'}
                {formatCurrency(Math.abs(transaction.amount))}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
